import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { isExpectedUserError } from "@/lib/sentry";
import { useAuth } from "@/contexts/AuthContext";
import { fetchUserGeneratedImages } from "@/services/db/generatedImages";
import { useRealtimeSync } from "@/hooks/useRealtimeSync";
import type { GenerationStatus, GenerationType } from "@shared/schemas/enums";
import { getResizedImageUrl, GRID_THUMBNAIL_WIDTH, GRID_THUMBNAIL_QUALITY } from "@/lib/imageUrl";

export interface GeneratedImage {
  id: string;
  image_url: string | null;
  reference_image_url: string | null;
  prompt: string | null;
  aspect_ratio: string | null;
  resolution: string | null;
  pipeline: GenerationType;
  model: string | null;
  batch_id: string | null;
  task_id: string | null;
  status: GenerationStatus;
  created_at: string;
  /** Cloudflare-resized thumbnail URL — always use for grid/gallery displays */
  displayUrl: string;
}

/** Attach computed displayUrl to a raw DB row */
function withDisplayUrl<T extends { image_url?: string | null }>(img: T): GeneratedImage {
  return {
    ...(img as unknown as Omit<GeneratedImage, "displayUrl">),
    displayUrl: getResizedImageUrl(img.image_url ?? "", GRID_THUMBNAIL_WIDTH, { quality: GRID_THUMBNAIL_QUALITY }),
  };
}

type ImagesById = Record<string, GeneratedImage>;

type OptimisticRemovalSnapshot = {
  removed: Array<{ id: string; image: GeneratedImage; index: number }>;
};

type GeneratedImagesContextValue = {
  byId: ImagesById;
  orderIds: string[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;

  // Optimistic helpers
  optimisticRemoveById: (id: string) => OptimisticRemovalSnapshot;
  optimisticRemoveByUrl: (url: string) => OptimisticRemovalSnapshot;
  rollbackRemoval: (snapshot: OptimisticRemovalSnapshot) => void;
};

const GeneratedImagesContext = createContext<GeneratedImagesContextValue | null>(null);

export function GeneratedImagesProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [byId, setById] = useState<ImagesById>({});
  const [orderIds, setOrderIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Prevent overlapping fetches (e.g., multiple pages mounting)
  const inFlightRef = useRef<Promise<void> | null>(null);

  const refetch = useCallback(async () => {
    if (!user) {
      setById({});
      setOrderIds([]);
      setLoading(false);
      setError(null);
      return;
    }

    if (inFlightRef.current) return inFlightRef.current;

    const p = (async () => {
      setLoading(true);
      try {
        const data = await fetchUserGeneratedImages(user.id);

        const nextById: ImagesById = {};
        const nextOrder: string[] = [];

        for (const img of data) {
          nextById[img.id] = withDisplayUrl(img);
          nextOrder.push(img.id);
        }

        setById(nextById);
        setOrderIds(nextOrder);
        setError(null);
      } catch (e) {
        // The `setError` path renders an inline UI message; capture the
        // underlying error so a sustained fetch failure (RLS regression,
        // Supabase outage) is alertable in Sentry instead of just shown to
        // the affected user. Expected user errors (connection-lost, transient
        // network) are filtered, and raw Supabase error objects are wrapped in
        // an Error so Sentry doesn't log "Object captured as exception".
        if (!isExpectedUserError(e)) {
          Sentry.captureException(
            e instanceof Error
              ? e
              : new Error(
                  (e as { message?: unknown })?.message
                    ? String((e as { message?: unknown }).message)
                    : "GeneratedImages fetch failed",
                ),
            {
              tags: { type: "generated_images_fetch_failure" },
            },
          );
        }
        setError(e instanceof Error ? e.message : "Failed to fetch images");
      } finally {
        setLoading(false);
        inFlightRef.current = null;
      }
    })();

    inFlightRef.current = p;
    return p;
  }, [user]);

  useEffect(() => {
    // Fetch once per session/user. Pages will reuse this shared store.
    void refetch();
  }, [refetch]);

  // Realtime subscription — keeps the store in sync with DB changes
  useRealtimeSync<GeneratedImage>({
    channelName: "images-store-updates",
    table: "generated_images",
    filter: `user_id=eq.${user?.id}`,
    enabled: !!user,
    onInsert: (img) => {
      setById((prev) => ({ ...prev, [img.id]: withDisplayUrl(img) }));
      setOrderIds((prev) => (prev.includes(img.id) ? prev : [img.id, ...prev]));
    },
    onUpdate: (img) => {
      setById((prev) => ({ ...prev, [img.id]: withDisplayUrl(img) }));
    },
    onDelete: (old) => {
      setById((prev) => {
        const { [old.id]: _, ...rest } = prev;
        return rest;
      });
      setOrderIds((prev) => prev.filter((x) => x !== old.id));
    },
  });

  const optimisticRemoveById = useCallback(
    (id: string): OptimisticRemovalSnapshot => {
      const img = byId[id];
      if (!img) return { removed: [] };
      const idx = orderIds.indexOf(id);

      setById((prev) => {
        if (!prev[id]) return prev;
        const { [id]: _, ...rest } = prev;
        return rest;
      });

      setOrderIds((prev) => {
        const i = prev.indexOf(id);
        if (i === -1) return prev;
        return [...prev.slice(0, i), ...prev.slice(i + 1)];
      });

      return { removed: [{ id, image: img, index: idx }] };
    },
    [byId, orderIds]
  );

  const optimisticRemoveByUrl = useCallback(
    (url: string): OptimisticRemovalSnapshot => {
      if (!url) return { removed: [] };

      // Find IDs to remove (should be 1, but keep safe)
      const idsToRemove: string[] = [];
      for (const id of orderIds) {
        const img = byId[id];
        if (img?.image_url === url) idsToRemove.push(id);
      }

      const removedSnapshots = idsToRemove.map((id) => optimisticRemoveById(id));
      return { removed: removedSnapshots.flatMap((s) => s.removed) };
    },
    [byId, orderIds, optimisticRemoveById]
  );

  const rollbackRemoval = useCallback((snapshot: OptimisticRemovalSnapshot) => {
    if (!snapshot.removed.length) return;

    setById((prev) => {
      const next = { ...prev };
      for (const r of snapshot.removed) {
        next[r.id] = r.image;
      }
      return next;
    });

    setOrderIds((prev) => {
      const next = [...prev];
      // Restore in ascending index order so indices remain correct
      const sorted = [...snapshot.removed].sort((a, b) => a.index - b.index);
      for (const r of sorted) {
        const idx = r.index;
        if (idx >= 0 && idx <= next.length) {
          next.splice(idx, 0, r.id);
        } else {
          next.unshift(r.id);
        }
      }
      return next;
    });
  }, []);

  const value = useMemo<GeneratedImagesContextValue>(
    () => ({
      byId,
      orderIds,
      loading,
      error,
      refetch,
      optimisticRemoveById,
      optimisticRemoveByUrl,
      rollbackRemoval,
    }),
    [
      byId,
      orderIds,
      loading,
      error,
      refetch,
      optimisticRemoveById,
      optimisticRemoveByUrl,
      rollbackRemoval,
    ]
  );

  return <GeneratedImagesContext.Provider value={value}>{children}</GeneratedImagesContext.Provider>;
}

export function useGeneratedImagesStore() {
  const ctx = useContext(GeneratedImagesContext);
  if (!ctx) {
    throw new Error("useGeneratedImagesStore must be used within GeneratedImagesProvider");
  }
  return ctx;
}
